import { useMemo, useState, useEffect, useRef } from 'react';
import { FiAlertTriangle, FiCheck, FiX, FiArrowUpRight, FiLock, FiMove } from 'react-icons/fi';
import { cpApi } from '../api/api';
import { formatCurrency } from '../utils/formatNumber';
import { formatNextSalaryHoldLine } from '../utils/salaryHoldDisplay';
import { CustomSelect } from './ui/CustomSelect';

interface WithdrawRequestWidgetProps {
  onClose: () => void;
  onSuccess?: () => void;
  availableBalance: number;
  /** Средства в холде (не выводятся до разхолда). */
  heldBalance?: number;
  /** ISO — ближайшее время разхолда личных средств. */
  nextSalaryHoldReleaseAt?: string | null;
}

const NETWORK_OPTIONS = [
  { value: 'HTX_UID', label: 'HTX UID' },
  { value: 'BYBIT_UID', label: 'Bybit UID' },
  { value: 'BEP20', label: 'BEP20' },
  { value: 'TRC20', label: 'TRC20' },
  { value: 'TON', label: 'TON' },
  { value: 'ERC20', label: 'ERC20' },
] as const;
type NetworkValue = (typeof NETWORK_OPTIONS)[number]['value'];

function isExchangeUidNetwork(network: string): boolean {
  return network === 'HTX_UID' || network === 'BYBIT_UID';
}

function exchangeUidLabel(network: string): string {
  if (network === 'BYBIT_UID') return 'Bybit UID';
  if (network === 'HTX_UID') return 'HTX UID';
  return 'UID';
}

const evmAddrRe = /^0x[a-fA-F0-9]{40}$/;
const trc20Re = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const tonRe = /^(EQ|UQ)[A-Za-z0-9_-]{46}$/;
const htxUIDRe = /^\d{4,20}$/;

function isWalletValid(network: string, wallet: string): boolean {
  const value = wallet.trim();
  if (!value) return false;
  if (network === 'BEP20' || network === 'ERC20') return evmAddrRe.test(value);
  if (network === 'TRC20') return trc20Re.test(value);
  if (network === 'TON') return tonRe.test(value);
  return false;
}

function isExchangeUIDValid(uid: string): boolean {
  return htxUIDRe.test(uid.trim());
}

export default function WithdrawRequestWidget({
  onClose,
  onSuccess,
  availableBalance,
  heldBalance = 0,
  nextSalaryHoldReleaseAt = null,
}: WithdrawRequestWidgetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 24, y: 90 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isPinned, setIsPinned] = useState(true);
  const dragRef = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
  const hasMovedRef = useRef(false);

  const [amount, setAmount] = useState(
    Number.isFinite(availableBalance) ? Math.max(0, availableBalance).toFixed(2) : ''
  );
  const [amountTouched, setAmountTouched] = useState(false);
  const [network, setNetwork] = useState<NetworkValue>('HTX_UID');
  const [wallet, setWallet] = useState('');
  const [exchangeUid, setExchangeUid] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  const amountNum = useMemo(() => {
    const parsed = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(parsed)) return null;
    return parsed;
  }, [amount]);

  const amountError = useMemo(() => {
    if (amountNum == null) return 'Введите сумму';
    if (amountNum <= 0) return 'Сумма должна быть больше нуля';
    if (amountNum > availableBalance) return 'Сумма не может быть больше доступного баланса';
    return '';
  }, [amountNum, availableBalance]);

  const holdReleaseLine = useMemo(
    () => (heldBalance > 0 ? formatNextSalaryHoldLine(nextSalaryHoldReleaseAt ?? null, heldBalance) : null),
    [heldBalance, nextSalaryHoldReleaseAt]
  );

  const isExchangeUid = isExchangeUidNetwork(network);
  const walletValid = useMemo(() => isWalletValid(network, wallet), [network, wallet]);
  const exchangeUidValid = useMemo(() => isExchangeUIDValid(exchangeUid), [exchangeUid]);
  const destinationValid = isExchangeUid ? exchangeUidValid : walletValid;
  const canSubmit = !loading && !amountError && destinationValid;

  useEffect(() => {
    // Автоподстановка суммы из текущего баланса при открытии/обновлении,
    // пока пользователь не начал редактировать поле вручную.
    if (amountTouched) return;
    if (Number.isFinite(availableBalance)) {
      setAmount(Math.max(0, availableBalance).toFixed(2));
    } else {
      setAmount('');
    }
  }, [availableBalance, amountTouched]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const width = 520;
    if (hasMovedRef.current || isMobile) return;
    setPos({ x: Math.max(8, window.innerWidth - width - 8), y: 74 });
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging || isPinned) return;
      const width = panelRef.current?.getBoundingClientRect().width ?? 520;
      const height = panelRef.current?.getBoundingClientRect().height ?? 420;
      const nextX = e.clientX - dragRef.current.offsetX;
      const nextY = e.clientY - dragRef.current.offsetY;
      const clampedX = Math.min(Math.max(8, nextX), window.innerWidth - width - 8);
      const clampedY = Math.min(Math.max(8, nextY), window.innerHeight - Math.min(height, window.innerHeight - 16) - 8);
      setPos({ x: clampedX, y: clampedY });
      hasMovedRef.current = true;
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPinned, isMobile]);

  const submit = async () => {
    if (!canSubmit || amountNum == null) return;
    setLoading(true);
    try {
      await cpApi.createWithdrawTask({
        amount: amountNum,
        network,
        wallet: isExchangeUid ? undefined : wallet.trim(),
        htx_uid: isExchangeUid ? exchangeUid.trim() : undefined,
        comment: comment.trim() || undefined,
      });
      window.dispatchEvent(
        new CustomEvent('showToast', {
          detail: { message: 'Задача на вывод создана', type: 'success' },
        })
      );
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      const msg =
        (typeof data === 'string' && data) ||
        (typeof data === 'object' && data && 'error' in data && typeof (data as { error?: unknown }).error === 'string'
          ? (data as { error: string }).error
          : (err as Error)?.message) ||
        'Ошибка создания задачи';
      window.dispatchEvent(
        new CustomEvent('showToast', {
          detail: { message: `Ошибка: ${msg}`, type: 'error' },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={isMobile ? 'fixed inset-0 z-50 flex items-center justify-center p-2' : 'fixed'}
      style={isMobile ? undefined : { left: pos.x, top: pos.y, zIndex: 50 }}
    >
      {isMobile && <div className="absolute inset-0 bg-black/45" onClick={onClose} />}
      <div
        ref={panelRef}
        className={`bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-2xl border-2 border-green-200 dark:border-green-800/40 ${
          isMobile ? 'relative z-10 w-full max-w-[560px] max-h-[92dvh]' : 'w-[520px] max-w-[calc(100vw-2rem)] max-h-[90vh]'
        } overflow-y-auto`}
      >
        <div
          className={`flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 select-none ${isPinned || isMobile ? 'cursor-default' : 'cursor-move'}`}
          onMouseDown={(e) => {
            if (e.button !== 0 || isPinned || isMobile) return;
            const rect = (panelRef.current?.getBoundingClientRect() ?? { left: pos.x, top: pos.y }) as DOMRect;
            dragRef.current.isDragging = true;
            dragRef.current.offsetX = e.clientX - rect.left;
            dragRef.current.offsetY = e.clientY - rect.top;
          }}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center">
              <FiArrowUpRight className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Вывод</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <button
                type="button"
                onClick={() => setIsPinned((v) => !v)}
                className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 dark:bg-gray-800/50 dark:hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                title={isPinned ? 'Открепить (разрешить перетаскивание)' : 'Закрепить (запретить перетаскивание)'}
              >
                {isPinned ? (
                  <FiLock className="w-5 h-5 text-green-700 dark:text-green-300 transition-transform duration-200 group-hover:scale-110" />
                ) : (
                  <FiMove className="w-5 h-5 text-green-700 dark:text-green-300 transition-transform duration-200 group-hover:scale-110" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 dark:bg-gray-800/50 dark:hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
              title="Закрыть"
            >
              <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
            </button>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
            <div>
              Доступно к выводу: <span className="font-semibold text-emerald-700 dark:text-emerald-300">{formatCurrency(availableBalance)}</span>
            </div>
            {heldBalance > 0 && (
              <div className="text-xs">
                В холде: <span className="font-medium">{formatCurrency(heldBalance)}</span>
                {holdReleaseLine && (
                  <span className="block mt-0.5 text-gray-500 dark:text-gray-400">{holdReleaseLine}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сумма</label>
            <input
              value={amount}
              onChange={(e) => {
                setAmountTouched(true);
                setAmount(e.target.value.replace(/[^\d.,]/g, ''));
              }}
              inputMode="decimal"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              placeholder="0.00"
            />
            {amountError && <p className="mt-1 text-xs text-red-500">{amountError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Сеть</label>
            <CustomSelect
              value={network}
              onChange={(value) => setNetwork(value as NetworkValue)}
              options={NETWORK_OPTIONS.map((item) => ({ ...item }))}
              className="w-full [&>button]:bg-white [&>button]:dark:bg-gray-900/30 [&>button]:border [&>button]:border-gray-300 [&>button]:dark:border-gray-600 [&>button]:text-gray-900 [&>button]:dark:text-gray-100 [&>button]:py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {isExchangeUid ? exchangeUidLabel(network) : 'Кошелек'}
            </label>
            <input
              value={isExchangeUid ? exchangeUid : wallet}
              onChange={(e) => {
                if (isExchangeUid) {
                  setExchangeUid(e.target.value.replace(/\D/g, ''));
                } else {
                  setWallet(e.target.value.trim());
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              placeholder={isExchangeUid ? `Введите ${exchangeUidLabel(network)}` : 'Введите адрес кошелька'}
            />
            {isExchangeUid && !exchangeUidValid && exchangeUid.trim() !== '' && (
              <p className="mt-1 text-xs text-orange-500">Некорректный {exchangeUidLabel(network)} (4–20 цифр)</p>
            )}
            {!isExchangeUid && !walletValid && wallet.trim() !== '' && (
              <p className="mt-1 text-xs text-orange-500">Некорректный формат адреса для сети {network}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Коммент (опционально)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 300))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              placeholder="Комментарий"
            />
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">{comment.length}/300</div>
          </div>

          {confirmStep && (
            <div className="rounded-xl border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 p-3">
              <div className="flex items-start gap-2 text-amber-800 dark:text-amber-200">
                <FiAlertTriangle className="w-4 h-4 mt-0.5" />
                <div className="text-sm">Уверены? Проверьте еще раз сумму, сеть и {isExchangeUid ? exchangeUidLabel(network) : 'адрес кошелька'}.</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            disabled={loading}
          >
            Отмена
          </button>
          {!confirmStep ? (
            <button
              type="button"
              onClick={() => setConfirmStep(true)}
              disabled={!canSubmit}
              className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Подтвердить
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiCheck className="w-4 h-4" />
              {loading ? 'Отправка...' : 'Да, отправить'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
